import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  IconButton,
  Collapse,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  ExpandMore,
  ExpandLess,
  Visibility,
  SignalCellular4Bar,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ResolveIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import apiService from '../services/api';
import MobileOutageDetailsModal from '../components/MobileOutageDetailsModal';
import MobileOutageFormDialog from '../components/MobileOutageFormDialog';

interface MobileOutage {
  id: string;
  carrier_id: string;
  carrier_name: string;
  incident_id: string;
  reference_number?: string;
  status: string;
  incident_type: string;
  severity: string;
  impact_description: string;
  affected_services?: string;
  affected_areas: string;
  start_time?: string;
  estimated_end_time?: string;
  resolved_time?: string;
  outage_start_time?: string;
  outage_end_time?: string;
  affected_service_type?: string;
  impact_level?: string;
  fault_location?: string;
  states?: string[];
  created_at: string;
  updated_at: string;
  affected_customer_count: number;
  last_update_time?: string;
}

interface MobileOutageDetails extends MobileOutage {
  affected_customers: Array<{
    id: string;
    name: string;
    email: string;
    service_id: string;
    impact_level: string;
    notified_at?: string;
  }>;
  notes?: string;
}

const MobileOutagesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [outages, setOutages] = useState<MobileOutage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [outageDetails, setOutageDetails] = useState<Record<string, MobileOutageDetails>>({});
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('active');
  const [carrierFilter, setCarrierFilter] = useState('all');
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOutageId, setSelectedOutageId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedOutage, setSelectedOutage] = useState<MobileOutage | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MobileOutage | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkResolveOpen, setBulkResolveOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const fetchOutages = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (carrierFilter !== 'all') params.carrier_id = carrierFilter;

      const data = await apiService.getMobileOutages(params);
      setOutages(data.filter((outage: MobileOutage) => outage && outage.id));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch mobile outages');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, carrierFilter]);

  useEffect(() => {
    fetchOutages();
  }, [fetchOutages]);

  // Handle incident_id query parameter to auto-open modal
  useEffect(() => {
    const incidentIdParam = searchParams.get('incident_id');
    if (incidentIdParam && outages.length > 0) {
      // Find the outage by incident_id
      const outage = outages.find((o) => o.incident_id === incidentIdParam);
      if (outage && outage.id) {
        // Use the database id (not incident_id) for the modal
        setSelectedOutageId(outage.id);
        setModalOpen(true);
        // Remove the query parameter from URL to clean it up
        searchParams.delete('incident_id');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [outages, searchParams, setSearchParams]);

  const fetchOutageDetails = async (outageId: string) => {
    if (outageDetails[outageId]) return;
    
    try {
      const data = await apiService.getMobileOutageDetails(outageId);
      setOutageDetails(prev => ({ ...prev, [outageId]: data }));
    } catch (err: any) {
      console.error('Failed to fetch outage details:', err);
    }
  };

  const handleRowToggle = (outageId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(outageId)) {
      newExpanded.delete(outageId);
    } else {
      newExpanded.add(outageId);
      fetchOutageDetails(outageId);
    }
    setExpandedRows(newExpanded);
  };

  const handleViewDetails = (outageId: string) => {
    if (!outageId || outageId.trim() === '') {
      console.error('Invalid outage ID');
      return;
    }
    setSelectedOutageId(outageId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOutageId(null);
  };

  const handleCreateOutage = () => {
    setFormMode('create');
    setSelectedOutage(null);
    setFormOpen(true);
  };

  const handleEditOutage = (outage: MobileOutage) => {
    setFormMode('edit');
    setSelectedOutage(outage);
    setFormOpen(true);
  };

  const handleDeleteOutage = (outage: MobileOutage) => {
    setDeleteTarget(outage);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOutage = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      setDeleteError(null);
      await apiService.deleteMobileOutage(deleteTarget.id);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchOutages();
    } catch (err: any) {
      setDeleteError(err.response?.data?.detail || err.message || 'Failed to delete outage');
    } finally {
      setDeleteLoading(false);
    }
  };

  const allSelected = outages.length > 0 && outages.every((o) => selectedIds.has(o.id));
  const someSelected = outages.some((o) => selectedIds.has(o.id)) && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(outages.map((o) => o.id)));
    }
  };

  const handleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const confirmBulkDelete = async () => {
    setBulkLoading(true);
    setBulkError(null);
    try {
      await apiService.bulkDeleteMobileOutages(Array.from(selectedIds));
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      await fetchOutages();
    } catch (err: any) {
      setBulkError(err.response?.data?.detail || err.message || 'Bulk delete failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const confirmBulkResolve = async () => {
    setBulkLoading(true);
    setBulkError(null);
    try {
      await apiService.bulkResolveMobileOutages(Array.from(selectedIds));
      setSelectedIds(new Set());
      setBulkResolveOpen(false);
      await fetchOutages();
    } catch (err: any) {
      setBulkError(err.response?.data?.detail || err.message || 'Bulk resolve failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'error';
      case 'resolved': return 'success';
      case 'monitoring': return 'warning';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      // Parse the UTC datetime and convert to Australian Eastern Time
      const utcDate = new Date(dateString);
      const sydneyDate = utcToZonedTime(utcDate, 'Australia/Sydney');
      return format(sydneyDate, 'dd/MM/yyyy HH:mm');
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SignalCellular4Bar /> Mobile Outages
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Monitor and manage mobile network outages from Vocus Optus Mobile. Create manual records for outages not supplied automatically.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateOutage}
            size="small"
          >
            Create Mobile Outage
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Filters</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Carrier"
                value={carrierFilter}
                onChange={(e) => setCarrierFilter(e.target.value)}
              >
                <MenuItem value="all">All Carriers</MenuItem>
                <MenuItem value="vocus-optus-mobile">Vocus Optus Mobile</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : outages.length === 0 ? (
        <Alert severity="info">No mobile outages found</Alert>
      ) : (
        <>
        {selectedIds.size > 0 && (
          <Paper sx={{ mb: 1, px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, bgcolor: 'primary.main' }}>
            <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600, flexGrow: 1 }}>
              {selectedIds.size} selected
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<ResolveIcon />}
              onClick={() => { setBulkError(null); setBulkResolveOpen(true); }}
            >
              Resolve
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => { setBulkError(null); setBulkDeleteOpen(true); }}
            >
              Delete
            </Button>
            <Button size="small" sx={{ color: '#fff' }} onClick={() => setSelectedIds(new Set())}>
              Clear
            </Button>
          </Paper>
        )}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAll}
                    sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' }, '&.MuiCheckbox-indeterminate': { color: '#fff' } }}
                  />
                </TableCell>
                <TableCell width="50px"></TableCell>
                <TableCell>Incident ID</TableCell>
                <TableCell>Carrier</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Affected Areas</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>Last Update</TableCell>
                <TableCell width="120px" align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outages.map((outage) => (
                <React.Fragment key={outage.id}>
                  <TableRow hover selected={selectedIds.has(outage.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(outage.id)}
                        onChange={() => handleSelectRow(outage.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleRowToggle(outage.id)}
                      >
                        {expandedRows.has(outage.id) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{outage.incident_id}</TableCell>
                    <TableCell>{outage.carrier_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={outage.status}
                        color={getStatusColor(outage.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={outage.severity}
                        color={getSeverityColor(outage.severity)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{outage.affected_areas}</TableCell>
                    <TableCell>{formatDateTime(outage.start_time)}</TableCell>
                    <TableCell>{formatDateTime(outage.last_update_time)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(outage.id)}
                          title="View details"
                        >
                          <Visibility />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handleEditOutage(outage)}
                          title="Edit outage"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteOutage(outage)}
                          title="Delete outage"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 0, border: 0 }}>
                      <Collapse in={expandedRows.has(outage.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Impact Description:
                          </Typography>
                          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-line' }}>
                            {outage.impact_description || 'No description available'}
                          </Typography>
                          {outageDetails[outage.id] && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Affected Customers ({outageDetails[outage.id].affected_customers?.length || 0}):
                              </Typography>
                              {outageDetails[outage.id].affected_customers?.length > 0 ? (
                                <ul>
                                  {outageDetails[outage.id].affected_customers.slice(0, 5).map((customer) => (
                                    <li key={customer.id}>
                                      {customer.name} - {customer.email}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No customers linked
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )}

      {/* Details Modal */}
      {selectedOutageId && (
        <MobileOutageDetailsModal
          open={modalOpen}
          onClose={handleCloseModal}
          outageId={selectedOutageId}
        />
      )}

      {/* Create / Edit Form */}
      <MobileOutageFormDialog
        open={formOpen}
        mode={formMode}
        outage={selectedOutage}
        onClose={() => setFormOpen(false)}
        onSuccess={async () => {
          await fetchOutages();
        }}
      />

      {/* Bulk Delete Confirmation */}
      <Dialog open={bulkDeleteOpen} onClose={bulkLoading ? undefined : () => setBulkDeleteOpen(false)}>
        <DialogTitle>Delete {selectedIds.size} outage{selectedIds.size !== 1 ? 's' : ''}?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              This will permanently delete <strong>{selectedIds.size}</strong> mobile outage{selectedIds.size !== 1 ? 's' : ''}. This action cannot be undone.
            </Typography>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteOpen(false)} disabled={bulkLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmBulkDelete} disabled={bulkLoading}>
            {bulkLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Resolve Confirmation */}
      <Dialog open={bulkResolveOpen} onClose={bulkLoading ? undefined : () => setBulkResolveOpen(false)}>
        <DialogTitle>Resolve {selectedIds.size} outage{selectedIds.size !== 1 ? 's' : ''}?</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              Mark <strong>{selectedIds.size}</strong> mobile outage{selectedIds.size !== 1 ? 's' : ''} as resolved?
              Outages already resolved will be skipped.
            </Typography>
            {bulkError && <Alert severity="error">{bulkError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkResolveOpen(false)} disabled={bulkLoading}>Cancel</Button>
          <Button color="success" variant="contained" onClick={confirmBulkResolve} disabled={bulkLoading}>
            {bulkLoading ? 'Resolving…' : 'Resolve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={deleteLoading ? undefined : () => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Mobile Outage</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography>
              Are you sure you want to delete outage{' '}
              <strong>{deleteTarget?.incident_id || 'this outage'}</strong>? This action cannot be undone.
            </Typography>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            color="error"
            onClick={confirmDeleteOutage}
            disabled={deleteLoading}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileOutagesPage;
